import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { ReactComponent as FilterIcon } from '../../images/SearchBar/filter.svg'
import SearchIcon from '../../images/SearchBar/search.svg'
import FilterDropdown from './FilterDropdown'
import ToggleSwitch from './ToggleSwitch'
import { DateRangePickerComponent } from './DateRangePicker'
import { DateWithTimeRangePicker } from './DateWithTimeRangePicker'

const SearchBarStyle = styled.div`
  display: flex;
  width: 100%;
  height: 45px;
  background: ${({ theme }) => theme.colors.bar};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.white};
  padding: 12px 20px;
  align-items: center;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.1);
`
const Number = styled.div`
  font-family: ${({ theme }) => theme.fonts.rajdhaniMedium};
  font-size: ${({ theme }) => theme.fontSize.s22};
`

const Type = styled.div`
  padding-inline: 5px 10px;
  font-family: ${({ theme }) => theme.fonts.regular};
  font-size: ${({ theme }) => theme.fontSize.s14};
`

const FilterSection = styled.div`
  display: flex;
  font-family: ${({ theme }) => theme.fonts.regular};
  font-size: ${({ theme }) => theme.fontSize.s12};
  color: ${({ theme }) => theme.colors.gray4};
  position: relative;

  span {
    padding-left: 5px;
    vertical-align: bottom;
  }
`
const StyledFilterDropdown = styled(FilterDropdown)`
  display: flex;
  width: auto;
  visibility: hidden;
  color: black;
`

const StyledToggleSwitch = styled(ToggleSwitch)`
  visibility: hidden;
  display: flex;
  margin-left: 10px;
`

const StyledDateRangePicker = styled(DateRangePickerComponent)`
  visibility: hidden;
  display: flex;
`

const StyledDateWithTimeRangePicker = styled(DateWithTimeRangePicker)`
  visibility: hidden;
  display: flex;
`

const Dropdown = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  position: absolute;
  z-index: 100;
  max-height: 0;
  transition: max-height 0.15s ease-out;

  ${({ isOpen }) =>
    isOpen &&
    css`
      max-height: 52px;
      transition: all 0.15s ease-in;

      ${StyledFilterDropdown}, ${StyledToggleSwitch}, ${StyledDateRangePicker},${StyledDateWithTimeRangePicker} {
        visibility: visible;
        transition: visibility 0s ease 0.15s;
      }
    `};

  top: 32px;
  width: auto;
  padding-inline: 10px;
  height: 52px;
  background: ${({ theme }) => theme.colors.bar};
  border-radius: 0 0 8px 8px;
  color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.1);
`

const Filter = styled.div<{ isOpen: boolean }>`
  padding-inline: 10px;
  cursor: pointer;

  ${({ isOpen }) =>
    isOpen &&
    css`
      color: ${({ theme }) => theme.colors.white};
      svg path {
        fill: ${({ theme }) => theme.colors.white};
      }
    `}
`

const Search = styled.div``

const SearchInput = styled.input<{ icon: string }>`
  background: transparent;
  background: url(${({ icon }) => icon}) no-repeat scroll;
  background-position: 2px;
  padding-left: 30px;
  color: ${({ theme }) => theme.colors.gray4};
  width: 295px;
  bottom: -6px;
  padding-block: 5px;
  border: 1px solid transparent;

  position: absolute;

  ::placeholder {
    color: ${({ theme }) => theme.colors.gray4};
  }
  ::-webkit-input-placeholder {
    /* Chrome/Opera/Safari */
    color: ${({ theme }) => theme.colors.gray4};
  }
  ::-moz-placeholder {
    /* Firefox 19+ */
    color: ${({ theme }) => theme.colors.gray4};
  }
  :-ms-input-placeholder {
    /* IE 10+ */
    color: ${({ theme }) => theme.colors.gray4};
  }
  :-moz-placeholder {
    /* Firefox 18- */
    color: ${({ theme }) => theme.colors.gray4};
  }

  :focus {
    border-bottom: ${({ theme }) => theme.colors.white} solid 1px;
    background-color: ${({ theme }) => `${theme.colors.white}26`};
    border-radius: 3px 3px 0 0;
  }
`
/**Checkbox options */
// const OPTIONS = ['One', 'Two', 'Three']
// const OPTIONS2 = ['1', '2', '3']
// const OPTIONS3 = ['1213-12', '2323-12', '3-123']

type Props = {
  filtersSet?: { id: string; name: string; filterType: string }[]
  filterOptions?: any[]
  onFilter?: (
    filter: {
      values: string[] | Record<string, never>
      columnId: string
      filterType: string
    }[]
  ) => void
}

const SearchBar: React.FC<Props> = ({
  onFilter,
  filtersSet,
  filterOptions,
}) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const [isSwitchChecked, setIsSwitchChecked] = useState(false)
  const [filters, setFilters] = useState<{
    [filterColumn: string]: {
      values: any
      columnId: string
      filterType: string
    }
  }>({})

  const toggleSwitch = () => setIsSwitchChecked(!isSwitchChecked)
  const toggle = () => setDropdownOpen(!isDropdownOpen)

  const handleFilter = (
    values: any,
    filterType: string,
    columnId: string
  ) => {
    setFilters({
      ...filters,
      [columnId]: { values, columnId, filterType },
    })
  }

  useEffect(() => {
    onFilter && onFilter(Object.values(filters))
  }, [filters])

  const filterComponents = filtersSet?.map((filter, key) => {
    return filter.filterType === 'dateRange' ? (
      <StyledDateRangePicker
        onFilterApply={(values) =>
          handleFilter(values, filter.filterType, filter.id)
        }
      />
    ) : (
      <StyledFilterDropdown
        key={key}
        onFilterApply={(values) =>
          handleFilter(values, filter.filterType, filter.id)
        }
        id={filter.id}
        name={filter.name}
        checkboxOptions={filterOptions?.[key][filter.id]}
      />
    )
  })

  return (
    <SearchBarStyle>
      <Number>22</Number>
      <Type>Incidents</Type>
      <FilterSection>
        <Filter isOpen={isDropdownOpen} onClick={toggle}>
          <FilterIcon />
          <span>Filters </span>
        </Filter>
        <Dropdown isOpen={isDropdownOpen}>
          {filterComponents}
          <StyledToggleSwitch
            isChecked={isSwitchChecked}
            onChange={toggleSwitch}
          />
        </Dropdown>
        <Search>
          <SearchInput icon={SearchIcon} placeholder={'Search'} />
        </Search>
      </FilterSection>
    </SearchBarStyle>
  )
}

export default SearchBar
